if (!customElements.get("low-power-video")) {
  customElements.define(
    "low-power-video",
    /*
      Wraps any block that uses a <video> tag. Has two responsibilities:

      1. Existing — click/touch handler for sections that opt-in via the
         `.wt-video` / `.hero--video-background` markers. Lets non-autoplay
         videos start on user interaction without each section wiring its
         own listener.

      2. Lazy-load autoplay videos via IntersectionObserver so below-the-fold
         sections don't kick off large HD mp4 downloads on initial page load.
         Above-the-fold autoplay videos are already intersecting when the
         observer attaches, so they resume almost immediately — only
         delaying playback by the IO callback latency (~tens of ms).

      We intentionally do NOT defer non-autoplay videos. Their default
      `preload=metadata` cost is tiny, and other code (e.g., the gallery
      swiper) expects `video.duration` to be available shortly after
      render. Stripping their sources would break that.
    */
    class LowPowerVideo extends HTMLElement {
      constructor() {
        super();
        this._observer = null;
        this._deferredVideos = [];
      }

      connectedCallback() {
        this.video_section = this.querySelector(".wt-video");
        this.video_background_section = this.querySelector(
          ".hero--video-background",
        );
        if (this.video_section) {
          this.checkAndPlayVideoSection(this.video_section);
        } else if (this.video_background_section) {
          this.checkAndPlayBackgroundVideoSection(
            this.video_background_section,
          );
        }

        this.setupLazyAutoplay();
      }

      setupLazyAutoplay() {
        const videos = Array.from(this.querySelectorAll("video")).filter(
          (v) =>
            v.hasAttribute("autoplay") || v.dataset.lpvAutoplay === "true",
        );
        if (videos.length === 0) return;

        videos.forEach((v) => {
          this.stripVideoForDeferral(v);
          this._deferredVideos.push(v);
        });

        if (!("IntersectionObserver" in window)) {
          // Legacy fallback: resume immediately so videos still work.
          this._deferredVideos.forEach((v) => this.resumeDeferredVideo(v));
          this._deferredVideos = [];
          return;
        }

        // Wait for `window.load` before activating the IntersectionObserver.
        // The browser's loading spinner (and LCP) stays active while any
        // subresource — including an above-the-fold autoplay video stream —
        // is in flight. A single 65 MB HD mp4 on a typical mobile connection
        // keeps the spinner up for ~25-30s, which is what users perceive as
        // a "slow" page. By gating the observer on `load`, the spinner
        // clears first, then any in-viewport videos start playing a moment
        // later (poster shown during the gap).
        let observerStarted = false;
        const startObserver = () => {
          if (observerStarted) return;
          observerStarted = true;
          this._observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                this.resumeDeferredVideo(entry.target);
                this._observer.unobserve(entry.target);
              });
            },
            {
              // Start loading slightly before the video scrolls into view
              // so playback begins without a buffering pause at typical
              // scroll speeds.
              rootMargin: "200px 0px",
              threshold: 0.01,
            },
          );
          this._deferredVideos.forEach((v) => this._observer.observe(v));
        };

        if (document.readyState === "complete") {
          startObserver();
        } else {
          window.addEventListener("load", startObserver, { once: true });
          // Safety net: if another resource holds `load` open forever
          // (a stuck tracker, dropped third-party request, etc.), don't
          // leave the videos paused indefinitely.
          setTimeout(startObserver, 5000);
        }
      }

      stripVideoForDeferral(video) {
        if (video.dataset.lpvDeferred === "true") return;

        if (video.hasAttribute("autoplay")) {
          video.dataset.lpvAutoplay = "true";
          video.removeAttribute("autoplay");
        }

        // Move every <source>'s src into data-lpv-src so the browser
        // can't queue any range requests. Sources without src render
        // the parent <video> as having no playable resource — nothing
        // is fetched.
        const sources = video.querySelectorAll("source[src]");
        sources.forEach((source) => {
          source.dataset.lpvSrc = source.getAttribute("src");
          source.removeAttribute("src");
        });

        video.preload = "none";
        video.dataset.lpvDeferred = "true";

        // Aborts any in-flight range request the browser may have started
        // between HTML parse and this script's `defer` execution.
        try {
          video.load();
        } catch (_) {
          // Some browsers throw transient errors on load() after mutating
          // sources — non-fatal.
        }
      }

      resumeDeferredVideo(video) {
        if (video.dataset.lpvDeferred !== "true") return;

        const sources = video.querySelectorAll("source[data-lpv-src]");
        sources.forEach((source) => {
          source.setAttribute("src", source.dataset.lpvSrc);
          delete source.dataset.lpvSrc;
        });

        // preload=metadata lets the browser fetch the duration/first
        // frame promptly. We don't force preload=auto — the autoplay
        // attribute (re-applied below) drives full loading.
        video.preload = "metadata";

        try {
          video.load();
        } catch (_) {}

        const wasAutoplay = video.dataset.lpvAutoplay === "true";
        if (wasAutoplay) {
          video.setAttribute("autoplay", "");
          delete video.dataset.lpvAutoplay;
          const p = video.play();
          if (p && typeof p.catch === "function") {
            p.catch(() => {
              // Autoplay rejected (e.g., browser policy or unmuted).
              // Leave paused; click/touch handlers can still start it.
            });
          }
        }

        delete video.dataset.lpvDeferred;
      }

      checkAndPlayBackgroundVideoSection(video_background_section) {
        const hero_overlay =
          video_background_section.querySelector(".hero__overlay");
        const video_element = video_background_section.querySelector("video");
        if (video_element) {
          hero_overlay.addEventListener("touchstart", () =>
            this.playVideo(video_element),
          );
          hero_overlay.addEventListener("click", () =>
            this.playVideo(video_element),
          );
        }
      }

      checkAndPlayVideoSection(video) {
        const video_element = video.querySelector("video");
        if (video_element) {
          video.addEventListener("touchstart", () =>
            this.playVideo(video_element),
          );
          video.addEventListener("click", () => this.playVideo(video_element));
        }
      }

      playVideo(video_element) {
        if (!video_element) return;
        // If a user clicks/taps before the observer fires (very fast scroll
        // or quick interaction with an above-the-fold video), restore the
        // sources first so play() has data to work with.
        if (video_element.dataset.lpvDeferred === "true") {
          this.resumeDeferredVideo(video_element);
        }
        if (!video_element.playing) {
          const p = video_element.play();
          if (p && typeof p.catch === "function") {
            p.catch(() => {});
          }
        }
      }

      disconnectedCallback() {
        if (this.video_section) {
          this.video_section.removeEventListener("touchstart", this.playVideo);
          this.video_section.removeEventListener("click", this.playVideo);
        }
        if (this.video_background_section) {
          const hero_overlay =
            this.video_background_section.querySelector(".hero__overlay");
          const video_element =
            this.video_background_section.querySelector("video");
          if (video_element) {
            hero_overlay.removeEventListener("touchstart", () =>
              this.playVideo(video_element),
            );
            hero_overlay.removeEventListener("click", () =>
              this.playVideo(video_element),
            );
          }
        }
        if (this._observer) {
          this._observer.disconnect();
          this._observer = null;
        }
        this._deferredVideos = [];
      }
    },
  );
}
